/**
 * usePlanCapabilities — P3.2 (capabilities.v1)
 *
 * Consome GET /me/context (backend: src/modules/entitlements) e expõe
 * as capabilities do PLANO do clube do usuário. O plano determina
 * "o clube possui este módulo?"; o papel determina o que o usuário
 * pode fazer nele (RBAC continua intocado).
 *
 * Política de visibilidade (FAIL-CLOSED para módulos opcionais):
 *  - loading sem cache → itens que dependem de capability ficam OCULTOS
 *    (core/navegação básica não depende de capability, continua visível);
 *  - loading com cache (localStorage) → aplica último contexto conhecido;
 *  - contexto carregado → aplica capabilities do backend
 *    (legacy_default vem EXPLÍCITO do backend com tudo ON — não é
 *    fail-open do React);
 *  - erro sem cache → módulos opcionais permanecem ocultos.
 * O enforcement duro continua no backend (403 CAPABILITY_NOT_IN_PLAN).
 */
import { useEffect, useState, useCallback } from 'react';
import { AZURE } from '../services/api';

const STORAGE_KEY = 'vukz_plan_context';
const TTL_MS = 5 * 60 * 1000;

// Cache em módulo (sidebar + mobile menu compartilham 1 fetch).
let _cache = null;
let _cacheAt = 0;
let _inflight = null;

const readStoredContext = () => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const storeContext = (ctx) => {
  try {
    if (ctx) localStorage.setItem(STORAGE_KEY, JSON.stringify(ctx));
  } catch { /* ignore */ }
};

const fetchContext = () => {
  const now = Date.now();
  if (_cache && now - _cacheAt < TTL_MS) return Promise.resolve(_cache);
  if (_inflight) return _inflight;
  _inflight = AZURE.get('/me/context')
    .then(({ data }) => {
      _cache = data?.data || null;
      _cacheAt = Date.now();
      storeContext(_cache);
      return _cache;
    })
    .catch(() => _cache || readStoredContext()) // erro → último contexto conhecido (ou null)
    .finally(() => { _inflight = null; });
  return _inflight;
};

export const usePlanCapabilities = () => {
  // Estado inicial: cache de módulo → localStorage → null (loading).
  const [ctx, setCtx] = useState(() => _cache || readStoredContext());

  useEffect(() => {
    let alive = true;
    fetchContext().then((c) => { if (alive && c) setCtx(c); });
    return () => { alive = false; };
  }, []);

  const capabilities = ctx?.capabilities || null;

  const hasPlanCapability = useCallback(
    (cap) => {
      // FAIL-CLOSED: sem contexto (loading sem cache / erro sem cache),
      // módulos opcionais ficam ocultos até o backend responder.
      if (!capabilities) return false;
      return capabilities[cap] === true;
    },
    [capabilities],
  );

  return {
    planContext: ctx,          // { club_id, roles, is_setup_admin, plan, capabilities, limits, source }
    planCapabilities: capabilities,
    planLoaded: !!ctx,
    hasPlanCapability,
  };
};

export default usePlanCapabilities;
